import React, { ChangeEvent, SetStateAction } from "react";
import { GridApi, RowNode } from "ag-grid-community";
import store from "../store";
import { updateIsExcelUpload } from "@features/isExcelUploadSlice";
import { sendRequest } from "./retrieve-data";
import { getActiveModule, titleCase } from "./utils";
import { saveAs } from "file-saver";
import { showToast } from "./modals";
import {
    IProductMappingListParams,
    IUploadExcelFile,
    ParentModule,
    SubModule,
    TCommonRowData,
    IClient,
} from "./common-types";
import { map } from "underscore";
import * as Sentry from "@sentry/react";
import {
    updateBulkTaxonomyDisplay,
    updateProductMappingFilterModel,
} from "@features/modalSlice";
import { updateProductMappingSelectionData } from "@/features/selectedDataSlice";

type TBackdropDisplay = {
    (value: SetStateAction<boolean>): void;
    (arg0: boolean): void;
};
type TActiveCheckedNode = RowNode[];
type TSetAssetFileName = {
    (value: SetStateAction<string>): void;
    (arg0: string): void;
};
interface TDownloadRequestedAsset {
    responseBuffer: Buffer;
    setAssetFileName: TSetAssetFileName;
}
interface IRequestAssetHandler {
    activeCheckedNode: TActiveCheckedNode;
    setAssetFileName: TSetAssetFileName;
    setResponseBuffer: React.Dispatch<SetStateAction<Buffer>>;
    setBackdropDisplay: TBackdropDisplay;
    productMappingGridParams?: Partial<IProductMappingListParams>;
}

const requestPortfoliosAsset = async (
    activeCheckedNode: TActiveCheckedNode
) => {
    const { isAllPortfoliosSelected, allPortfolioId } = store.getState();

    const selectedPortfoliosId = isAllPortfoliosSelected.value
        ? allPortfolioId.value
        : activeCheckedNode.map((portfolio) =>
              Number(portfolio.data["portfolio_id"])
          );
    try {
        const response = await sendRequest({
            type: `portfolios/taxonomy`,
            method: "post",
            data: {
                portfolios: selectedPortfoliosId,
                record_type: "portfolio",
            },
        });
        if (response.status !== 200) {
            throw new Error((await response.json()).message);
        }
        return response;
    } catch (err) {
        console.error(err);
        showToast("danger", (err as Error).message);
        Sentry.captureException(err);
    }
};

const requestCampaignsAsset = async (activeCheckedNode: TActiveCheckedNode) => {
    const { isAllCampaignsSelected, allSysCampaignId } = store.getState();

    const selectedCampaignsId = isAllCampaignsSelected.value
        ? allSysCampaignId.value
        : activeCheckedNode.map((campaign) => campaign.data["sys_campaign_id"]);
    try {
        const response = await sendRequest({
            type: `campaigns/taxonomy`,
            method: "post",
            data: {
                campaigns: selectedCampaignsId,
                record_type: "campaign",
            },
        });
        if (response.status !== 200) {
            throw new Error((await response.json()).message);
        }
        return response;
    } catch (err) {
        console.error(err);
        showToast("danger", (err as Error).message);
        Sentry.captureException(err);
    }
};

const requestAdgroupsAsset = async (activeCheckedNode: TActiveCheckedNode) => {
    const { isAllAdgroupsSelected, allSysAdgroupId } = store.getState();

    const selectedAdgroupsId = isAllAdgroupsSelected.value
        ? allSysAdgroupId.value
        : activeCheckedNode.map((adgroup) => adgroup.data["sys_adgroup_id"]);
    try {
        const response = await sendRequest({
            type: `adgroups/taxonomy`,
            method: "post",
            data: {
                adgroups: selectedAdgroupsId,
                record_type: "adgroup",
            },
        });
        if (response.status !== 200) {
            throw new Error((await response.json()).message);
        }
        return response;
    } catch (err) {
        console.error(err);
        showToast("danger", (err as Error).message);
        Sentry.captureException(err);
    }
};

const requestProductMappingAsset = async (
    activeCheckedNode: TCommonRowData,
    productMappingGridParams?: Partial<IProductMappingListParams>
) => {
    try {
        const selectedSkus = activeCheckedNode
            ? map(activeCheckedNode, (node) => node.sku)
            : ([] as any);
        const filterModel = productMappingGridParams?.filterModel || {};

        if (selectedSkus.length > 0) {
            filterModel.sku = {
                values: selectedSkus,
                filterType: "set",
            };
        }

        const requestData: IProductMappingListParams = {
            startRow: 0,
            endRow: 0,
            sortModel: [],
            ...productMappingGridParams,
            ...(selectedSkus.length === 0 ? { startRow: 0, endRow: 0 } : {}),
            filterModel,
        };

        const response = await sendRequest({
            type: `products/taxonomy`,
            method: "post",
            data: requestData,
        });
        if (response.status !== 200) {
            throw new Error((await response.json()).message);
        }
        return response;
    } catch (err) {
        console.error(err);
        showToast("danger", (err as Error).message);
        Sentry.captureException(err);
    }
};

export const requestAsset = async (
    activeCheckedNode: TActiveCheckedNode | TCommonRowData,
    productMappingGridParams?: Partial<IProductMappingListParams>
) => {
    const { activeModule } = getActiveModule();
    store.dispatch(updateIsExcelUpload(true));
    let response;
    switch (activeModule) {
        case SubModule.PORTFOLIO:
            response = requestPortfoliosAsset(
                activeCheckedNode as TActiveCheckedNode
            );
            break;
        case SubModule.CAMPAIGN:
            response = requestCampaignsAsset(
                activeCheckedNode as TActiveCheckedNode
            );
            break;
        case SubModule.ADGROUP:
            response = requestAdgroupsAsset(
                activeCheckedNode as TActiveCheckedNode
            );
            break;
        case ParentModule.SETTINGS:
            response = requestProductMappingAsset(
                activeCheckedNode as TCommonRowData,
                productMappingGridParams
            );
    }
    return response;
};

export const setBulkTaxonomyFileName = (): string => {
    const { activeModule } = getActiveModule();

    const activeClientId = +store.getState().activeClientId.value;
    const clientData: IClient[] = store.getState().clients.value;
    const activeClientData = clientData.find(
        (data) => data.id === activeClientId
    );
    const clientName = activeClientData?.name || "";

    const clientNameSlug = clientName
        .replace(/[().]/g, "")
        .split(" ")
        .filter(Boolean)
        .join("-");

    const moduleSegment =
        activeModule === ParentModule.SETTINGS
            ? "Pfm-Bulk-Taxonomy-Update"
            : `${titleCase(activeModule)}-Bulk-Taxonomy-Update`;

    return `Reviniti-${clientNameSlug}-${`${
        activeClientData.country_non_iso || activeClientData.country_iso
    }`}-${moduleSegment}`;
};

export const requestAssetHandler = async ({
    activeCheckedNode,
    setAssetFileName,
    setResponseBuffer,
    setBackdropDisplay,
    productMappingGridParams,
}: IRequestAssetHandler) => {
    try {
        const response = await requestAsset(
            activeCheckedNode,
            productMappingGridParams
        );
        if (response.status !== 200) {
            throw new Error((await response.json()).message);
        }
        const responseBuffer = await response.arrayBuffer();
        setResponseBuffer(responseBuffer);
        const downloadedFileName = downloadRequestedAsset({
            responseBuffer,
            setAssetFileName,
        });
        showToast(
            "success",
            `Requested file ${downloadedFileName} has been downloaded succesfully.`
        );
    } catch (err) {
        console.error(err);
        showToast("danger", (err as Error).message);
        setBackdropDisplay(false);
        Sentry.captureException(err);
    }
};

export const downloadRequestedAsset = ({
    responseBuffer,
    setAssetFileName,
}: TDownloadRequestedAsset) => {
    const blob = new Blob([responseBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const fileName = setBulkTaxonomyFileName();
    setAssetFileName(`${fileName}.xlsx`);
    saveAs(blob, `${fileName}.xlsx`);
    return `${fileName}.xlsx`;
};

export const uploadAsset = async (excelData: FormData) => {
    const response = await sendRequest(
        {
            type: "taxonomy",
            method: "post",
            data: excelData,
        },
        true,
        false,
        true,
        "multipart/form-data"
    );
    return response;
};

export const submitUploadedFile = async (
    e: React.SyntheticEvent<HTMLButtonElement>,
    uploadExcelFile: IUploadExcelFile,
    setBackdropDisplay: TBackdropDisplay,
    productMappingGridApi?: GridApi
) => {
    e.preventDefault();
    store.dispatch(updateIsExcelUpload(true));
    const formData = new FormData();
    formData.append("file", uploadExcelFile, uploadExcelFile.name);
    try {
        const response = await uploadAsset(formData);
        setBackdropDisplay(false);
        if (response.status !== 200) {
            throw new Error(response?.message);
        }

        // Refresh the grid data for product mapping
        if (
            getActiveModule().activeModule === ParentModule.SETTINGS &&
            productMappingGridApi
        ) {
            productMappingGridApi.deselectAll();
            store.dispatch(updateProductMappingSelectionData([]));
            store.dispatch(updateProductMappingFilterModel({}));
            store.dispatch(updateIsExcelUpload(false));
            store.dispatch(updateBulkTaxonomyDisplay(false));
            productMappingGridApi.refreshServerSideStore({
                route: [],
                purge: true,
            });
        }

        showToast(
            "success",
            `${uploadExcelFile.name} has been uploaded successfully`
        );
    } catch (err) {
        console.log(`Failed to upload ${uploadExcelFile.name}`);
        console.error(err);
        showToast("danger", `Failed to upload ${uploadExcelFile.name}`);
        setBackdropDisplay(false);
        Sentry.captureException(err);
    }
};

export const uploadChangeHandler = (
    e: ChangeEvent<HTMLInputElement>,
    setUploadExcelFile: React.Dispatch<React.SetStateAction<IUploadExcelFile>>
) => {
    e.preventDefault();
    setUploadExcelFile(e.target.files[0]);
};
